import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { safeReadJSON } from '@/lib/utils';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('employee_token'));

  useEffect(() => {
    const verifyToken = async () => {
      const storedToken = localStorage.getItem('employee_token');
      const parsedEmployee = safeReadJSON('employee_data', null);

      if (storedToken && parsedEmployee) {
        try {
          // First, set employee from localStorage for immediate UI
          setEmployee(parsedEmployee);
          setToken(storedToken);
          
          // Then verify token in background with a timeout
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
          
          const response = await axios.get(`${API}/employee/me`, {
            headers: { Authorization: `Bearer ${storedToken}` },
            signal: controller.signal
          });
          
          clearTimeout(timeoutId);
          
          // Update with fresh data from server
          setEmployee(response.data);
        } catch (err) {
          // Only clear on auth errors, not network/timeout errors
          if (err.response?.status === 401 || err.response?.status === 403) {
            localStorage.removeItem('employee_token');
            localStorage.removeItem('employee_data');
            setEmployee(null);
            setToken(null);
          }
          // For network errors, keep using localStorage data
        }
      } else if (storedToken && !parsedEmployee) {
        // Token exists but employee data is malformed/missing — clear stale state.
        localStorage.removeItem('employee_token');
        localStorage.removeItem('employee_data');
      }
      setLoading(false);
    };

    verifyToken();
  }, []);

  const login = (accessToken, employeeData) => {
    localStorage.setItem('employee_token', accessToken);
    localStorage.setItem('employee_data', JSON.stringify(employeeData));
    setToken(accessToken);
    setEmployee(employeeData);
  };

  const logout = async () => {
    try {
      if (token) {
        await axios.post(`${API}/employee/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      localStorage.removeItem('employee_token');
      localStorage.removeItem('employee_data');
      setToken(null);
      setEmployee(null);
    }
  };

  const hasRole = (allowedRoles) => {
    if (!employee) return false;
    return allowedRoles.includes(employee?.role);
  };

  const hasAccess = (page) => {
    if (!employee) return false;

    const role = employee?.role;
    
    // Admin has access to everything
    if (role === 'Admin') return true;
    
    // Define page access by role.
    //  - Everyone can see inventory (dealership-wide stock visibility).
    //  - Only Admin + Sales Manager see analytics / reports / costs / gross.
    //  - Sales Staff are intentionally minimal: they work deals and customers
    //    assigned to them. The backend scopes list endpoints by employee_id
    //    so even direct API access returns their own records only.
    const accessMap = {
      'Sales Manager': [
        'dashboard', 'sales-pipeline', 'credit-application', 'customers',
        'calculator', 'retail-worksheet', 'analytics', 'sales-staff', 'sales',
        'delivery', 'customer-messages', 'trade-in-estimator', 'email-campaigns',
        'customer-portal-management', 'inventory', 'brokered-deals', 'accounting',
        'promotions', 'broker-portal',
      ],
      'Used Car Manager': ['inventory', 'brokered-deals'],
      'Accountant': ['accounting', 'inventory', 'broker-portal'],
      'Sales Staff': [
        'welcome', 'sales-pipeline', 'inventory', 'calculator',
        'retail-worksheet', 'trade-in-estimator', 'customers', 'sales',
        'credit-application',
      ],
      // Offsite Broker: their own isolated workspace only — no dealership data.
      'Broker': ['broker'],
    };

    return accessMap[role]?.includes(page) || false;
  };

  const value = {
    employee,
    token,
    loading,
    login,
    logout,
    hasRole,
    hasAccess,
    isAuthenticated: !!employee
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
